import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Image,
  RefreshControl,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  Alert,
  Modal,
  ScrollView,
} from 'react-native';
// SafeAreaView from -context (NOT react-native) because we use the `edges`
// prop, which the react-native built-in SafeAreaView silently ignores.
import { SafeAreaView } from 'react-native-safe-area-context';
import { FlashList } from '@shopify/flash-list';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { ItemValuation } from '../components/common/ItemValuation';
import { itemDisplayValue, itemValuationTotals } from '../lib/itemValuation';
import { EmptyState } from '../components/common/EmptyState';
import { QuickStartBanner } from '../components/common/QuickStartBanner';
import { usePendingValuationRefresh } from '../hooks/usePendingValuationRefresh';
import PortfolioHeader from '../components/PortfolioHeader';
import ShareCardGenerator from '../components/ShareCardGenerator';
import type { CacheStackParamList } from '../types/navigation';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { ConditionBadge } from '../components/common/ConditionBadge';
import { isOwnedStatus, isRemovedStatus } from '../constants/itemStatus';
import { fetchUserProducts, needsAccountRefetch } from '../store/slices/userProductsSlice';
import {
  CacheListItemData,
  ProductCategory,
  categorizeProducts,
  categorizeProduct,
  getOwnedProducts,
  getTrackingProducts,
  categorizeProductsByStatus,
} from '../store/slices/userProductsSlice';

interface Props {
  navigation: any;
}

interface CacheListItemProps {
  item: CacheListItemData;
  navigation: any;
  isDuplicate?: boolean;
  workPending?: boolean;
}

const CacheListItem: React.FC<CacheListItemProps> = ({ item, navigation, isDuplicate = false, workPending }) => {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.md,
          marginBottom: theme.spacing.md,
          // Horizontal margin (instead of padding on listContentContainer) so
          // PortfolioHeader and the other ListHeader sections can keep their
          // own edge-flush layouts without inheriting list padding.
          marginHorizontal: theme.spacing.lg,
          overflow: 'hidden',
          minHeight: 100,
        },
        cardDuplicate: {
          borderColor: 'transparent',
          borderLeftColor: '#A8895A',
          borderLeftWidth: 3,
        },
        thumbnail: {
          width: 80,
          height: 80,
          marginRight: theme.spacing.md,
          borderRadius: theme.radius.radiusSm,
        },
        thumbnailPlaceholder: {
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        cardLeftColumn: {
          flex: 1,
          justifyContent: 'center',
          paddingVertical: theme.spacing.xxs,
        },
        cardRightColumn: {
          alignItems: 'flex-end',
          justifyContent: 'center',
          marginLeft: theme.spacing.md,
        },
        itemName: {
          fontFamily: theme.fontFamily.bodyMedium,
          fontSize: 15,
          lineHeight: 20,
          color: theme.colors.textPrimary,
        },
        subtitle: {
          fontFamily: theme.fontFamily.body,
          fontSize: 12,
          lineHeight: 16,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
        },
        badgeRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          marginTop: theme.spacing.xs,
          flexWrap: 'wrap',
        },
        reanalyzeButton: {
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          paddingVertical: theme.spacing.xxs,
          paddingHorizontal: theme.spacing.sm,
          backgroundColor: 'transparent',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusSm,
          marginTop: theme.spacing.xs,
          gap: theme.spacing.xs,
        },
        reanalyzeButtonText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
          letterSpacing: 0,
        },
      }),
    [theme],
  );
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [reanalyzing, setReanalyzing] = useState(false);

  useEffect(() => {
    let isMounted = true; // Prevent state update on unmounted component
    const fetchImageUrl = async () => {
      // Check if we have images in the new format
      const images = item.images as any;
      if (images && Array.isArray(images) && images.length > 0) {
        try {
          // Handle image object with storage_path property
          const imagePath = images[0].storage_path || images[0];
          const { data } = supabase.storage
            .from('item-images')
            .getPublicUrl(imagePath);
          if (isMounted && data?.publicUrl) {
            setImageUrl(data.publicUrl);
          }
        } catch (error) {
          console.error(`Error getting image URL for item ${item.id}:`, error);
          if (isMounted) {
            setImageUrl(null); // Reset on error
          }
        }
      }
    };

    fetchImageUrl();

    return () => { isMounted = false; }; // Cleanup on unmount
  }, [item.images, item.id]); // Re-run if images or item ID changes

  // Get the model, family, and brand names for display
  const displayName = item.brandName && item.modelName ? `${item.brandName} ${item.modelName}` : 'Product';

  // Check if this item needs review (for showing reanalyze button)
  const itemCategory = categorizeProduct(item);
  const needsReview = itemCategory === 'needs_review';

  const handleReanalyze = async () => {
    if (!imageUrl || reanalyzing) return;

    setReanalyzing(true);
    try {
      // Navigate to the reanalysis screen (now in the same CacheStack)
      navigation.navigate('ReanalyzeItem', {
        itemId: item.id,
        imageUrl: imageUrl,
        currentItem: item,
      });
    } catch (error) {
      console.error('Error starting reanalysis:', error);
    } finally {
      setReanalyzing(false);
    }
  };

  const subtitleParts = [
    item.familyName,
    isDuplicate ? 'Possible duplicate' : null,
  ].filter(Boolean);

  return (
    <Pressable
      style={[styles.card, isDuplicate && styles.cardDuplicate]}
      onPress={() => navigation.navigate('ItemDetail', { itemId: item.id })}
    >
      {imageUrl ? (
        <Image
          source={{ uri: imageUrl }}
          style={styles.thumbnail}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.thumbnail, styles.thumbnailPlaceholder]}>
          <Ionicons name="image-outline" size={40} color={theme.colors.textTertiary} />
        </View>
      )}

      {/* Left column: name, subtitle, badges, action chips */}
      <View style={styles.cardLeftColumn}>
        <Text style={styles.itemName} numberOfLines={1}>
          {displayName}
        </Text>

        {subtitleParts.length > 0 && (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitleParts.join(' · ')}
          </Text>
        )}

        <View style={styles.badgeRow}>
          <ConditionBadge condition={item.condition} />
        </View>

        {needsReview && imageUrl && (
          <TouchableOpacity
            style={styles.reanalyzeButton}
            onPress={handleReanalyze}
            disabled={reanalyzing}
          >
            <Ionicons name="refresh" size={14} color={theme.colors.accent} />
            <Text style={styles.reanalyzeButtonText}>
              {reanalyzing ? 'Analyzing...' : 'Reanalyze'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Right column: the item's own valuation state (V3 P6) — never the catalogue headline */}
      <View style={styles.cardRightColumn}>
        <ItemValuation item={item} workPending={workPending} compact={true} />
      </View>
    </Pressable>
  );
};

interface Props {
  navigation: any;
  route?: {
    params?: {
      activeTab?: ProductCategory;
    };
  };
}

export default function CacheScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        centered: {
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        controlsRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
          paddingVertical: theme.spacing.md,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        searchBox: {
          backgroundColor: theme.colors.backgroundElevated,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
          flex: 1,
        },
        sortButton: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
        },
        sortButtonText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        listContentContainer: {
          // Horizontal padding moved to each card's marginHorizontal so the
          // ListHeader chrome (PortfolioHeader, action center, controls,
          // filters) can run edge-to-edge with their own internal padding.
          // paddingTop is zero — the ListHeaderComponent should sit flush with
          // the safe area; gap before the first card lives in listHeaderSpacer.
          paddingBottom: 100,
        },
        listHeaderSpacer: {
          height: theme.spacing.xl,
        },
        errorText: {
          ...theme.typography.md,
          color: theme.colors.destructive,
          textAlign: 'center',
          marginBottom: theme.spacing.lg,
        },
        message: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.lg,
          marginTop: theme.spacing.md,
        },
        statusDropdownContainer: {
          paddingHorizontal: theme.spacing.lg,
          paddingVertical: theme.spacing.sm,
          backgroundColor: theme.colors.backgroundPrimary,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        statusDropdownButton: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        statusDropdownButtonText: {
          flex: 1,
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        dropdownOverlay: {
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          justifyContent: 'flex-start',
          paddingTop: 120,
          paddingHorizontal: theme.spacing.lg,
        },
        dropdownMenu: {
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          overflow: 'hidden',
        },
        dropdownItem: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.md,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        dropdownItemActive: {
          backgroundColor: theme.colors.accent,
        },
        dropdownItemText: {
          flex: 1,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        dropdownItemTextActive: {
          color: theme.colors.backgroundPrimary,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        filterChipsContainer: {
          backgroundColor: theme.colors.backgroundPrimary,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        filterChipsScrollContent: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
          paddingVertical: theme.spacing.sm,
        },
        filterChip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusLg,
        },
        filterChipActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        filterChipText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        filterChipTextActive: {
          color: theme.colors.backgroundPrimary,
        },
        duplicateBannerBottom: {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundElevated,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.colors.accent,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          gap: theme.spacing.sm,
          zIndex: 10,
        },
        duplicateBannerText: {
          flex: 1,
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        sortOption: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: 'transparent',
          marginBottom: theme.spacing.sm,
        },
        sortOptionActive: {
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.backgroundSurface,
        },
        sortOptionText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
        sortOptionTextActive: {
          color: theme.colors.textPrimary,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        actionCenter: {
          marginHorizontal: theme.spacing.lg,
          marginBottom: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        actionCenterHeader: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        actionItem: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        actionItemContent: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.md,
          flex: 1,
        },
        actionItemText: {
          flex: 1,
        },
        actionItemTitle: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xxs,
        },
        actionItemSubtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );
  const { session } = useAuth();
  const dispatch = useDispatch<any>(); // Use any for dispatch type if needed
  const { items: storedItems, loading, error } = useSelector((state: RootState) => state.userProducts);
  const accountRefetch = useSelector((state: RootState) => session ? needsAccountRefetch(state.userProducts, session.user.id) : false);
  const items = useMemo(() => storedItems.filter(item => item.user_id === session?.user.id), [storedItems, session?.user.id]);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatusTab, setActiveStatusTab] = useState<'owned' | 'removed'>('owned');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [activeQualityFilter, setActiveQualityFilter] = useState<'all' | 'verified' | 'needs_review' | 'duplicates'>('all');
  const [filteredItems, setFilteredItems] = useState<CacheListItemData[]>(items);
  const [showDuplicateBanner, setShowDuplicateBanner] = useState(false);
  const [sortBy, setSortBy] = useState<'price_high' | 'price_low' | 'date_new' | 'date_old'>('date_new');
  const [showSortModal, setShowSortModal] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [topValuedItems, setTopValuedItems] = useState<{ name: string; value: number }[]>([]);
  const [portfolioItemCount, setPortfolioItemCount] = useState(0);
  const [portfolioTotalValue, setPortfolioTotalValue] = useState(0);
  const isFocused = useIsFocused();

  const fetchCache = useCallback(async () => {
    console.log('fetchCache called, session:', session?.user?.id ? 'User authenticated' : 'No user');
    if (!session) {
      console.log('No session, skipping cache fetch');
      setFilteredItems([]);
      return;
    }

    try {
      console.log('Dispatching fetchUserProducts with user ID:', session.user.id);
      const result = await dispatch(fetchUserProducts(session.user.id));
      const succeeded = fetchUserProducts.fulfilled.match(result);
      return succeeded;
    } catch (error) {
      console.error('Error fetching user products:', error);
      return false;
    }
  }, [session, dispatch]);

  const workStatus = usePendingValuationRefresh(Boolean(session) && items.some(item => item.restoration_pending), isFocused, async () => fetchCache(), session?.user.id, items.filter(item => item.restoration_valuation && item.restoration_pending).map(item => item.id));
  useEffect(() => { if (isFocused && session) void fetchCache(); }, [isFocused, fetchCache]);
  // An old account's in-flight thunk can defer the new account's focus fetch.
  useEffect(() => {
    if (isFocused && accountRefetch) void fetchCache();
  }, [isFocused, accountRefetch, fetchCache]);

  // Aggregate share-card totals + fetch price deltas whenever items change.
  // Share-card aggregation runs unconditionally — gating it on variantIds
  // (as a previous version did) hid totals for users whose items have no
  // variant_id yet. Totals come from owned items so they line up with
  // PortfolioHeader's getVerifiedPortfolioValue source.
  useEffect(() => {
    if (items.length === 0) return;

    // V3 P6: totals use only each item's displayed value (itemValuationDisplay); an
    // unvalued item is counted, never priced from catalogue or legacy state. Per-item
    // price deltas from the variant's catalogue history are gone from item surfaces
    // (DESIGN_V3 §5.5); item valuation history arrives with the P8 projector.
    const ownedItems = items.filter(item => isOwnedStatus(item.status));
    const sortedByValue = ownedItems
      .filter(i => (itemDisplayValue(i) ?? 0) > 0)
      .sort((a, b) => (itemDisplayValue(b) ?? 0) - (itemDisplayValue(a) ?? 0));
    setTopValuedItems(
      sortedByValue.slice(0, 3).map(i => ({
        name: `${i.brandName || 'Unknown'} ${i.modelName || 'Item'}`,
        value: itemDisplayValue(i) ?? 0,
      }))
    );
    setPortfolioTotalValue(itemValuationTotals(ownedItems).total);
    setPortfolioItemCount(ownedItems.length);
  }, [items]);

  // Update active filter when screen comes into focus with route params
  useFocusEffect(
    useCallback(() => {
      if (route?.params?.activeTab) {
        setActiveQualityFilter(route.params.activeTab as 'verified' | 'needs_review');
      }
    }, [route?.params?.activeTab])
  );

  // Filter and sort items based on search query, active tab, and sort option
  useEffect(() => {
    let itemsForActiveTab: CacheListItemData[] = [];

    if (items.length > 0) {
      // First filter by status (owned vs removed)
      // Handle both current and legacy status values for backward compatibility
      let statusFiltered: CacheListItemData[] = [];
      if (activeStatusTab === 'owned') {
        statusFiltered = items.filter(item => isOwnedStatus(item.status));
      } else if (activeStatusTab === 'removed') {
        statusFiltered = items.filter(item => isRemovedStatus(item.status));
      }

      // Then categorize by quality (only for owned items)
      if (activeStatusTab === 'owned') {
        const categorization = categorizeProducts(statusFiltered);

        // Apply quality filter
        if (activeQualityFilter === 'all') {
          // Show all owned items (verified + needs review)
          itemsForActiveTab = [...categorization.verified, ...categorization.needs_review];
        } else if (activeQualityFilter === 'duplicates') {
          itemsForActiveTab = categorization.duplicates;
        } else {
          itemsForActiveTab = categorization[activeQualityFilter];
        }
      } else {
        // For removed items, show all
        itemsForActiveTab = statusFiltered;
      }

      // Duplicate detection is now handled by filter chips
      // No need for separate banner
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      itemsForActiveTab = itemsForActiveTab.filter(item =>
        item.brandName?.toLowerCase().includes(query) ||
        item.modelName?.toLowerCase().includes(query) ||
        item.familyName?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sortedItems = [...itemsForActiveTab].sort((a, b) => {
      switch (sortBy) {
        case 'price_high':
          return (itemDisplayValue(b) ?? 0) - (itemDisplayValue(a) ?? 0);
        case 'price_low':
          return (itemDisplayValue(a) ?? 0) - (itemDisplayValue(b) ?? 0);
        case 'date_new':
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case 'date_old':
          return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        default:
          return 0;
      }
    });

    setFilteredItems(sortedItems);
  }, [searchQuery, items, activeStatusTab, activeQualityFilter, sortBy]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCache();
    setRefreshing(false);
  }, [fetchCache]);

  const renderItem = ({ item }: { item: CacheListItemData }) => {
    // Check if this item is in the duplicates list
    const isDuplicate = categorization.duplicates.some(dupItem => dupItem.id === item.id);
    return <CacheListItem item={item} workPending={workStatus[item.id]} navigation={navigation} isDuplicate={isDuplicate} />;
  };

  const handleAddItem = () => {
    // Navigate to AddItem in MainStack (need to go up through CacheNavigator -> TabNavigator -> MainNavigator)
    const tabNav = navigation.getParent(); // Gets TabNavigator
    if (tabNav) {
      const mainNav = tabNav.getParent(); // Gets MainNavigator
      if (mainNav) {
        mainNav.navigate('AddItem' as never);
      }
    }
  };

  const handleFindDuplicates = () => {
    // Get only owned items (same filter used for duplicate detection in the UI)
    const ownedItems = items.filter(item => isOwnedStatus(item.status));

    if (ownedItems.length < 2) {
      Alert.alert('Not Enough Items', 'You need at least 2 items to detect duplicates.');
      return;
    }

    // Check if there are actually duplicates in the owned items
    // This ensures consistency with the duplicate count shown in the UI
    const duplicateItems = categorization.duplicates;

    if (duplicateItems.length === 0) {
      Alert.alert('No Duplicates', 'No duplicate items found in your cache.');
      return;
    }

    // Navigate to parent navigator (RootNavigator) to access MergeDuplicates
    const parentNav = navigation.getParent();
    if (parentNav) {
      // Pass only owned items so the detection algorithm finds the same groups
      // that are shown in the duplicates filter
      parentNav.navigate('MergeDuplicates' as never, { items: ownedItems } as never);
    }
  };

  if (loading === 'pending' && items.length === 0) {
    return (
      <SafeAreaView style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
        <Text style={styles.message}>Loading your cache...</Text>
      </SafeAreaView>
    );
  }

  if (!session) {
    return (
      <SafeAreaView style={[styles.container, styles.centered]}>
        <Text style={commonStyles.screenTitle}>My Cache</Text>
        <Text style={styles.message}>Please sign in to view your cache</Text>
        <TouchableOpacity
          style={commonStyles.primaryButton}
          onPress={() => navigation.navigate('SignIn')}
        >
          <Text style={commonStyles.primaryButtonText}>Sign In</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={[styles.container, styles.centered]}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={commonStyles.secondaryButton} onPress={() => void fetchCache()}>
          <Text style={commonStyles.secondaryButtonText}>Retry</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // Get categorization counts for tab labels
  const categorization = categorizeProducts(items);
  const tabCounts = {
    verified: categorization.verified.length,
    needs_review: categorization.needs_review.length,
    duplicates: categorization.duplicates.length,
  };

  // Everything that used to live as siblings above the FlashList now renders
  // inside its ListHeaderComponent so the entire screen scrolls as one unit.
  // Without this the chart + action centre + filters consumed enough vertical
  // space on smaller screens (e.g. iPhone 17) that only a sliver of the items
  // list remained visible.
  //
  // Built as a JSX element (not a function component) and passed directly to
  // ListHeaderComponent so FlashList renders the element via React's normal
  // type-based reconciliation. A function-component reference would change
  // identity on every render and FlashList would unmount/remount the entire
  // header subtree per keystroke — losing TextInput focus on the search box.
  const listHeaderElement = (
    <>
      {session && items.length > 0 && (
        <PortfolioHeader
          userId={session.user.id}
          onSharePress={() => setShowShareCard(true)}
          valuationRevision={items}
          active={isFocused}
        />
      )}

      {items.length > 0 && (tabCounts.needs_review > 0 || tabCounts.duplicates > 0) && (
        <View style={styles.actionCenter}>
          <View style={styles.actionCenterHeader}>
            <Ionicons name="notifications" size={20} color={theme.colors.textPrimary} />
            <Text style={commonStyles.sectionLabel}>
              Needs Your Attention ({tabCounts.needs_review + tabCounts.duplicates})
            </Text>
          </View>

          {tabCounts.needs_review > 0 && (
            <TouchableOpacity
              style={styles.actionItem}
              onPress={() => setActiveQualityFilter('needs_review')}
            >
              <View style={styles.actionItemContent}>
                <Ionicons name="alert-circle" size={20} color={theme.colors.accent} />
                <View style={styles.actionItemText}>
                  <Text style={styles.actionItemTitle}>{tabCounts.needs_review} items need review</Text>
                  <Text style={styles.actionItemSubtitle}>Tap to verify details</Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}

          {tabCounts.duplicates > 0 && (
            <TouchableOpacity
              style={styles.actionItem}
              onPress={() => setActiveQualityFilter('duplicates')}
            >
              <View style={styles.actionItemContent}>
                <Ionicons name="copy" size={20} color={theme.colors.textSecondary} />
                <View style={styles.actionItemText}>
                  <Text style={styles.actionItemTitle}>{tabCounts.duplicates} potential duplicates</Text>
                  <Text style={styles.actionItemSubtitle}>Tap to resolve</Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {items.length <= 2 && !searchQuery && (
        <QuickStartBanner
          itemCount={items.length}
          onAddItem={handleAddItem}
        />
      )}

      {items.length > 0 && (
        <View style={styles.controlsRow}>
          <TextInput
            placeholder="Search"
            placeholderTextColor={theme.colors.textTertiary}
            style={styles.searchBox}
            value={searchQuery}
            onChangeText={setSearchQuery}
            clearButtonMode="while-editing"
          />
          <TouchableOpacity
            style={styles.sortButton}
            onPress={() => setShowSortModal(true)}
          >
            <Ionicons name="funnel" size={18} color={theme.colors.textPrimary} />
            <Text style={styles.sortButtonText}>
              {sortBy === 'price_high' ? 'Price ↓' :
               sortBy === 'price_low' ? 'Price ↑' :
               sortBy === 'date_new' ? 'Newest' : 'Oldest'}
            </Text>
            <Ionicons name="chevron-down" size={14} color={theme.colors.textPrimary} />
          </TouchableOpacity>
        </View>
      )}

      {items.length > 0 && (
        <>
          <View style={styles.statusDropdownContainer}>
            <TouchableOpacity
              style={styles.statusDropdownButton}
              onPress={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <Ionicons
                name={activeStatusTab === 'owned' ? 'cube' : 'trash-outline'}
                size={18}
                color={theme.colors.textPrimary}
              />
              <Text style={styles.statusDropdownButtonText}>
                {activeStatusTab === 'owned' ? 'My Cache' : 'Removed'}
              </Text>
              <Ionicons name="chevron-down" size={16} color={theme.colors.textPrimary} />
            </TouchableOpacity>
          </View>

          {activeStatusTab === 'owned' && (
            <View style={styles.filterChipsContainer}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.filterChipsScrollContent}
              >
                <TouchableOpacity
                  style={[styles.filterChip, activeQualityFilter === 'all' && styles.filterChipActive]}
                  onPress={() => setActiveQualityFilter('all')}
                >
                  <Text style={[styles.filterChipText, activeQualityFilter === 'all' && styles.filterChipTextActive]}>
                    All
                  </Text>
                </TouchableOpacity>

                {tabCounts.needs_review > 0 && (
                  <TouchableOpacity
                    style={[styles.filterChip, activeQualityFilter === 'needs_review' && styles.filterChipActive]}
                    onPress={() => setActiveQualityFilter('needs_review')}
                  >
                    <Ionicons
                      name="alert-circle"
                      size={14}
                      color={activeQualityFilter === 'needs_review' ? theme.colors.backgroundPrimary : theme.colors.accent}
                    />
                    <Text style={[styles.filterChipText, activeQualityFilter === 'needs_review' && styles.filterChipTextActive]}>
                      Needs Info ({tabCounts.needs_review})
                    </Text>
                  </TouchableOpacity>
                )}

                {tabCounts.duplicates > 0 && (
                  <TouchableOpacity
                    style={[styles.filterChip, activeQualityFilter === 'duplicates' && styles.filterChipActive]}
                    onPress={() => setActiveQualityFilter('duplicates')}
                  >
                    <Ionicons
                      name="copy"
                      size={14}
                      color={activeQualityFilter === 'duplicates' ? theme.colors.backgroundPrimary : theme.colors.textSecondary}
                    />
                    <Text style={[styles.filterChipText, activeQualityFilter === 'duplicates' && styles.filterChipTextActive]}>
                      Duplicates ({tabCounts.duplicates})
                    </Text>
                  </TouchableOpacity>
                )}
              </ScrollView>
            </View>
          )}

          {/* Bottom of ListHeader: spacer that opens the gap before the first card */}
          <View style={styles.listHeaderSpacer} />
        </>
      )}
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FlashList
        data={filteredItems.filter(item => item.user_id === session?.user.id)}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        estimatedItemSize={110}
        contentContainerStyle={styles.listContentContainer as any}
        ListHeaderComponent={listHeaderElement}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.accent}
          />
        }
        ListEmptyComponent={() => {
          if (loading === 'pending') return null;

          // Search results empty state
          if (searchQuery) {
            return (
              <EmptyState
                icon="search-outline"
                title="No matches found"
                description={`No items match "${searchQuery}". Try a different search term.`}
                secondaryAction={{
                  label: 'Clear search',
                  onPress: () => setSearchQuery(''),
                }}
              />
            );
          }

          // Empty state based on active status tab
          if (activeStatusTab === 'removed') {
            return (
              <EmptyState
                icon="trash-outline"
                title="No removed items"
                description="Items you remove from your cache will appear here. You can restore them anytime."
              />
            );
          }

          // Owned items empty states
          if (activeQualityFilter === 'all' || activeQualityFilter === 'verified') {
            return (
              <EmptyState
                icon="checkmark-circle-outline"
                title="Your cache is empty"
                description="Start building your cache by adding items. We'll identify and value them instantly."
                primaryAction={{
                  label: 'Add Your First Item',
                  onPress: handleAddItem,
                }}
                tips={[
                  "Start with electronics - they're easiest to identify",
                  'Take clear photos in good lighting',
                  'Each item takes less than 30 seconds to add',
                  'Your portfolio value updates automatically',
                ]}
              />
            );
          }

          // Needs review tab empty state
          return (
            <EmptyState
              icon="information-circle-outline"
              title="All items verified!"
              description="Great job! All your items have been successfully identified and valued."
              secondaryAction={{
                label: 'View all items',
                onPress: () => setActiveQualityFilter('all'),
              }}
            />
          );
        }}
      />

      {/* Status Dropdown Menu — sibling of FlashList so the modal portal sits
          outside the scroll view */}
      {showStatusDropdown && (
        <Modal
          visible={showStatusDropdown}
          transparent
          animationType="fade"
          onRequestClose={() => setShowStatusDropdown(false)}
        >
          <TouchableOpacity
            style={styles.dropdownOverlay}
            activeOpacity={1}
            onPress={() => setShowStatusDropdown(false)}
          >
            <View style={styles.dropdownMenu}>
              <TouchableOpacity
                style={[styles.dropdownItem, activeStatusTab === 'owned' && styles.dropdownItemActive]}
                onPress={() => {
                  setActiveStatusTab('owned');
                  setShowStatusDropdown(false);
                }}
              >
                <Ionicons
                  name="cube"
                  size={20}
                  color={activeStatusTab === 'owned' ? theme.colors.backgroundPrimary : theme.colors.textSecondary}
                />
                <Text style={[styles.dropdownItemText, activeStatusTab === 'owned' && styles.dropdownItemTextActive]}>
                  My Cache
                </Text>
                {activeStatusTab === 'owned' && (
                  <Ionicons name="checkmark" size={20} color={theme.colors.backgroundPrimary} />
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.dropdownItem, activeStatusTab === 'removed' && styles.dropdownItemActive]}
                onPress={() => {
                  setActiveStatusTab('removed');
                  setShowStatusDropdown(false);
                }}
              >
                <Ionicons
                  name="trash-outline"
                  size={20}
                  color={activeStatusTab === 'removed' ? theme.colors.backgroundPrimary : theme.colors.textSecondary}
                />
                <Text style={[styles.dropdownItemText, activeStatusTab === 'removed' && styles.dropdownItemTextActive]}>
                  Removed
                </Text>
                {activeStatusTab === 'removed' && (
                  <Ionicons name="checkmark" size={20} color={theme.colors.backgroundPrimary} />
                )}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Duplicate Detection Banner - Bottom */}
      {showDuplicateBanner && (
        <TouchableOpacity
          style={styles.duplicateBannerBottom}
          onPress={handleFindDuplicates}
        >
          <Ionicons name="warning" size={20} color={theme.colors.accent} />
          <Text style={styles.duplicateBannerText}>
            {categorization.duplicates.length} potential duplicate{categorization.duplicates.length !== 1 ? 's' : ''}
          </Text>
          <Ionicons name="chevron-forward" size={20} color={theme.colors.accent} />
        </TouchableOpacity>
      )}

      {/* Sort Modal */}
      <Modal
        visible={showSortModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowSortModal(false)}
      >
        <TouchableOpacity
          style={commonStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowSortModal(false)}
        >
          <View style={commonStyles.modalContent}>
            <Text style={commonStyles.modalTitle}>Sort by</Text>

            {([
              { key: 'date_new', label: 'Newest First' },
              { key: 'date_old', label: 'Oldest First' },
              { key: 'price_high', label: 'Price: High to Low' },
              { key: 'price_low', label: 'Price: Low to High' },
            ] as const).map(option => (
              <TouchableOpacity
                key={option.key}
                style={[styles.sortOption, sortBy === option.key && styles.sortOptionActive]}
                onPress={() => {
                  setSortBy(option.key);
                  setShowSortModal(false);
                }}
              >
                <Text style={[styles.sortOptionText, sortBy === option.key && styles.sortOptionTextActive]}>
                  {option.label}
                </Text>
                {sortBy === option.key && (
                  <Ionicons name="checkmark" size={20} color={theme.colors.accent} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Share Card Modal */}
      <ShareCardGenerator
        visible={showShareCard}
        onClose={() => setShowShareCard(false)}
        totalValue={portfolioTotalValue}
        itemCount={portfolioItemCount}
        topItems={topValuedItems}
      />
    </SafeAreaView>
  );
}
